import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../comps/Icon';
import { groupsList, permissionsList, employeeList, addEmployee } from '../services/permissions';
import '../style/css/permissions2026.css';

/*
 * Permissions — STAFF MANAGEMENT + PERMISSION-LEVELS (2026 redesign).
 *
 * Renders inside the Settings modal (the 'הרשאות' panel — see SettingsModal.js).
 * It is PANEL CONTENT only (no app header/sidebar of its own).
 *
 * REAL DATA — wired to the existing PHP acts via src/services/permissions.js:
 *   - roles matrix  → groupsList (the business's permission groups)
 *                     + permissionsList (capability × granting-groups)
 *   - staff list    → employeeList (returns { employeesList, groupsList })
 *   - add employee  → addEmployee (creates a pending SMS-invite for phone + group)
 *
 * Mutations WITHOUT a backend act (change an employee's group, disable/enable,
 * remove, cancel a pending invite) are surfaced DISABLED with a reason — no fake
 * success. Wire them up here once the matching acts land.
 */

/* ------------------------------------------------------------------ *
 *  Helpers
 * ------------------------------------------------------------------ */

// Full display name from the employee row (privateName + lastName).
const employeeName = (e) =>
  [e.privateName, e.lastName].filter(Boolean).join(' ').trim() || e.groupName || 'עובד';

const NO_MUTATION_ACT =
  'שינוי תפקיד, השבתה והסרה יופעלו כשנוסיף את הפעולה בצד־השרת.';

/* ------------------------------------------------------------------ *
 *  Component
 * ------------------------------------------------------------------ */

const Permissions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Real backend state.
  const [roles, setRoles] = useState([]);        // groupsList → [{ GroupName, Permissions }]
  const [caps, setCaps] = useState([]);           // permissionsList → [{ permissionName, groupName }]
  const [employees, setEmployees] = useState([]); // employeeList.employeesList
  const [groups, setGroups] = useState([]);       // employeeList.groupsList → [{ groupID(enc), name }]

  // Add-employee form.
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ phone: '', employeeGroup: '' });
  const [adding, setAdding] = useState(false);
  const [addMsg, setAddMsg] = useState(null); // { ok:boolean, text:string }

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [rolesRes, capsRes, staffRes] = await Promise.all([
        groupsList(),
        permissionsList(),
        employeeList(),
      ]);
      setRoles(rolesRes);
      setCaps(capsRes);
      setEmployees(staffRes.employees);
      setGroups(staffRes.groups);
      if (!rolesRes.length && !staffRes.employees.length) {
        // Nothing came back at all — most likely a permission/auth issue.
        setError('לא ניתן לטעון עובדים והרשאות כרגע.');
      }
    } catch (e) {
      setError('אירעה שגיאה בטעינת הנתונים.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Role column keys for the matrix: prefer real groups (have encrypted id + name),
  // fall back to the groupsList names.
  const roleColumns = useMemo(() => {
    if (groups.length) return groups.map((g) => ({ key: g.groupID, name: g.name }));
    return roles.map((r) => ({ key: r.GroupName, name: r.GroupName }));
  }, [groups, roles]);

  // Default the add-form group to the first available group once loaded.
  useEffect(() => {
    if (groups.length && !form.employeeGroup) {
      setForm((f) => ({ ...f, employeeGroup: groups[0].groupID }));
    }
  }, [groups, form.employeeGroup]);

  /* ---- add employee (the one real mutation) ---- */
  const submitAdd = async (e) => {
    e.preventDefault();
    const phone = form.phone.trim();
    if (!phone || !form.employeeGroup || adding) return;
    setAdding(true);
    setAddMsg(null);
    const res = await addEmployee({ phone, employeeGroup: form.employeeGroup });
    setAdding(false);
    if (res.ok) {
      setAddMsg({ ok: true, text: 'ההזמנה נשלחה. העובד יופיע ברשימה לאחר שיאשר.' });
      setForm({ phone: '', employeeGroup: groups[0]?.groupID || '' });
      setShowAdd(false);
      load(); // reload the real staff list
    } else if (res.code === 416) {
      setAddMsg({ ok: false, text: 'משתמש זה כבר עובד בעסק.' });
    } else if (res.code === 417) {
      setAddMsg({ ok: false, text: 'תפקיד לא תקין. בחרו תפקיד מהרשימה.' });
    } else {
      setAddMsg({ ok: false, text: 'שליחת ההזמנה נכשלה. נסו שוב.' });
    }
  };

  /* ---- a single staff row ---- */
  const renderRow = (e) => {
    const name = employeeName(e);
    const key = e.userGroupID || e.userID || name;
    return (
      <div className="pm-row status-active" key={key}>
        <div className="pm-cell pm-cell-person" data-label="עובד">
          <span className="pm-avatar" aria-hidden="true">{name.charAt(0)}</span>
          <span className="pm-person-txt">
            <span className="pm-name">{name}</span>
            {e.businessNames?.name && (
              <span className="pm-phone">{e.businessNames.name}</span>
            )}
          </span>
        </div>

        <div className="pm-cell" data-label="תפקיד">
          <label className="pm-role-select">
            <span className="pm-vh">תפקיד עבור {name}</span>
            {/* No backend act to change an employee's group yet — read-only. */}
            <select value={e.groupName || ''} disabled title={NO_MUTATION_ACT} readOnly>
              <option value={e.groupName || ''}>{e.groupName || '—'}</option>
            </select>
          </label>
        </div>

        <div className="pm-cell" data-label="סטטוס">
          <span className="pm-badge badge-active">פעיל</span>
        </div>

        <div className="pm-cell pm-cell-actions" data-label="פעולות">
          {/* No disable/remove acts yet — controls disabled with a reason (no fake success). */}
          <button type="button" className="pm-btn-ghost" disabled title={NO_MUTATION_ACT}>
            השבתה
          </button>
          <button type="button" className="pm-btn-ghost pm-btn-danger" disabled title={NO_MUTATION_ACT}>
            הסרה
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="pm-page">
      {/* ---------------- Intro ---------------- */}
      <header className="pm-intro">
        <h1 className="pm-title">עובדים והרשאות</h1>
        <p className="pm-sub">
          נהל את הצוות שלך, קבע לכל עובד רמת הרשאה, והזמן עובדים חדשים למערכת ב-SMS.
        </p>
      </header>

      {loading ? (
        <div className="pm-empty">
          <p>טוען עובדים והרשאות…</p>
        </div>
      ) : error ? (
        <div className="pm-empty">
          <span className="pm-empty-emoji" aria-hidden="true"><Icon name="info" size={30} /></span>
          <p>{error}</p>
          <button type="button" className="pm-btn-ghost" onClick={load}>נסה שוב</button>
        </div>
      ) : (
        <>
          {/* ---------------- Roles / permission matrix ---------------- */}
          <section className="pm-section" aria-label="רמות הרשאה">
            <div className="pm-section-head">
              <h2 className="pm-section-title">רמות הרשאה</h2>
              <p className="pm-section-desc">מה כל תפקיד יכול לעשות במערכת.</p>
            </div>

            {roles.length > 0 && (
              <div className="pm-roles">
                {roles.map((r) => (
                  <article className="pm-role-card" key={r.GroupName}>
                    <span className="pm-role-name">{r.GroupName}</span>
                    <span className="pm-role-desc">{r.Permissions || 'אין הרשאות מוגדרות.'}</span>
                  </article>
                ))}
              </div>
            )}

            {/* Matrix: capabilities (rows) × roles (columns). */}
            {caps.length > 0 && roleColumns.length > 0 ? (
              <div className="pm-matrix" role="table" aria-label="מטריצת הרשאות">
                <div className="pm-matrix-head" role="row">
                  <span className="pm-matrix-cap" role="columnheader">יכולת</span>
                  {roleColumns.map((r) => (
                    <span className="pm-matrix-role" role="columnheader" key={r.key}>{r.name}</span>
                  ))}
                </div>
                {caps.map((c) => {
                  // groupName is a comma-joined list of the groups that grant this capability.
                  const grantNames = String(c.groupName || '')
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean);
                  return (
                    <div className="pm-matrix-row" role="row" key={c.ID || c.permissionName}>
                      <span className="pm-matrix-cap" role="rowheader">{c.permissionName}</span>
                      {roleColumns.map((r) => {
                        const ok = grantNames.includes(r.name);
                        return (
                          <span
                            className={`pm-matrix-cell ${ok ? 'is-yes' : 'is-no'}`}
                            role="cell"
                            key={r.key}
                            data-role={r.name}
                            aria-label={`${r.name}: ${ok ? 'מותר' : 'לא מותר'}`}
                          >
                            {ok ? '✓' : '—'}
                          </span>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="pm-empty">
                <p>אין הגדרות הרשאה להצגה.</p>
              </div>
            )}
          </section>

          {/* ---------------- Staff list ---------------- */}
          <section className="pm-section" aria-label="רשימת עובדים">
            <div className="pm-section-head pm-section-head-row">
              <div>
                <h2 className="pm-section-title">הצוות</h2>
                <p className="pm-section-desc">{employees.length} עובדים פעילים בצוות.</p>
              </div>
              <button
                type="button"
                className="pm-btn-primary"
                onClick={() => { setShowAdd((v) => !v); setAddMsg(null); }}
                aria-expanded={showAdd}
                disabled={!groups.length}
                title={!groups.length ? 'אין תפקידים זמינים להוספת עובד.' : undefined}
              >
                ＋ הוספת עובד
              </button>
            </div>

            {/* Add-employee inline form (real addEmployee act). */}
            {showAdd && (
              <form className="pm-add-form" onSubmit={submitAdd}>
                <div className="pm-add-grid">
                  <label className="pm-field">
                    <span className="pm-field-label">טלפון</span>
                    <input
                      type="tel"
                      dir="ltr"
                      value={form.phone}
                      onChange={(ev) => setForm({ ...form, phone: ev.target.value })}
                      placeholder="05X-XXXXXXX"
                      autoFocus
                    />
                  </label>
                  <label className="pm-field">
                    <span className="pm-field-label">תפקיד</span>
                    <select
                      value={form.employeeGroup}
                      onChange={(ev) => setForm({ ...form, employeeGroup: ev.target.value })}
                    >
                      {groups.map((g) => (
                        <option key={g.groupID} value={g.groupID}>{g.name}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="pm-add-note">
                  <span aria-hidden="true" style={{ display: 'inline-flex', verticalAlign: 'middle' }}><Icon name="mail" size={16} /></span> העובד יקבל הזמנה ב-SMS להצטרפות. הוא יופיע ברשימה לאחר שיאשר.
                </p>
                {addMsg && (
                  <p className={`pm-add-note ${addMsg.ok ? '' : 'pm-btn-danger'}`}>{addMsg.text}</p>
                )}
                <div className="pm-add-actions">
                  <button
                    type="button"
                    className="pm-btn-ghost"
                    onClick={() => { setShowAdd(false); setAddMsg(null); }}
                  >
                    ביטול
                  </button>
                  <button
                    type="submit"
                    className="pm-btn-primary"
                    disabled={!form.phone.trim() || !form.employeeGroup || adding}
                  >
                    {adding ? 'שולח…' : 'שליחת הזמנה'}
                  </button>
                </div>
              </form>
            )}

            {/* Add-result message when the form is closed. */}
            {!showAdd && addMsg && (
              <p className={`pm-add-note ${addMsg.ok ? '' : 'pm-btn-danger'}`} style={{ margin: '0 0 var(--s-4)' }}>
                {addMsg.text}
              </p>
            )}

            {/* Staff table (desktop table / mobile stacked cards). */}
            {employees.length > 0 ? (
              <div className="pm-table" role="table" aria-label="עובדים">
                <div className="pm-row pm-row-head" role="row">
                  <span className="pm-cell" role="columnheader">עובד</span>
                  <span className="pm-cell" role="columnheader">תפקיד</span>
                  <span className="pm-cell" role="columnheader">סטטוס</span>
                  <span className="pm-cell pm-cell-actions" role="columnheader">פעולות</span>
                </div>
                {employees.map(renderRow)}
              </div>
            ) : (
              <div className="pm-empty">
                <span className="pm-empty-emoji" aria-hidden="true"><Icon name="mail" size={30} /></span>
                <p>אין עדיין עובדים. הוסיפו עובד כדי לראות אותו כאן.</p>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default Permissions;
